/**
 * Keyboard cheat sheet overlay (item #83 Phase 2).
 *
 * Opens with Cmd-? (or Ctrl-?). Renders all registered shortcuts grouped
 * by context, with a search filter. Auto-generated from shortcut-registry.ts
 * so it never goes stale.
 *
 * Vim shortcuts are filtered out unless the user has opted in (item #84).
 */
import { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Search, Keyboard, ToggleLeft, ToggleRight } from 'lucide-react';
import { SHORTCUTS, CONTEXT_LABEL, getVimEnabled, setVimEnabled, type ShortcutContext } from '../../lib/shortcut-registry.js';

interface ShortcutCheatSheetProps {
  open: boolean;
  onClose: () => void;
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono font-medium bg-surface-sunken border border-line text-ink">
      {children}
    </kbd>
  );
}

export default function ShortcutCheatSheet({ open, onClose }: ShortcutCheatSheetProps) {
  const [search, setSearch] = useState('');
  const [vim, setVim] = useState<boolean>(() => getVimEnabled());

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Filter + group
  const grouped = useMemo(() => {
    const filtered = SHORTCUTS.filter((s) => {
      if (s.vimOnly && !vim) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return s.keys.toLowerCase().includes(q) || s.label.toLowerCase().includes(q);
    });
    const result: Record<ShortcutContext, typeof SHORTCUTS> = {} as Record<ShortcutContext, typeof SHORTCUTS>;
    for (const s of filtered) {
      const arr = result[s.context] ?? [];
      arr.push(s);
      result[s.context] = arr;
    }
    return result;
  }, [search, vim]);

  function handleVimToggle() {
    const next = !vim;
    setVim(next);
    setVimEnabled(next);
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 bg-ink/40 backdrop-blur-sm z-modal-overlay"
            onClick={onClose}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: -8 }}
            transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
            role="dialog"
            aria-label="Keyboard shortcuts"
            style={{
              left: 'calc(var(--workspace-left, 0px) + (100vw - var(--workspace-left, 0px) - var(--workspace-right-pad, 0px)) / 2)',
              transform: 'translateX(-50%)',
            }}
            className="
              fixed top-[8%] sm:top-[10%]
              w-[calc(100vw-1.5rem)] max-w-2xl max-h-[84vh] sm:max-h-[80vh] flex flex-col
              bg-surface-raised rounded-xl shadow-2xl border border-line
              overflow-hidden z-modal
            "
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-line">
              <Keyboard size={18} className="text-primary" />
              <h2 className="text-base font-semibold text-ink flex-1">Keyboard shortcuts</h2>
              <button
                onClick={handleVimToggle}
                className="inline-flex items-center gap-1.5 text-xs text-ink-muted hover:text-ink transition-colors"
                aria-label={vim ? 'Disable vim keys' : 'Enable vim keys'}
              >
                {vim ? <ToggleRight size={18} className="text-primary" /> : <ToggleLeft size={18} />}
                Vim keys
              </button>
              <button
                onClick={onClose}
                className="p-1 rounded-md hover:bg-surface-sunken text-ink-muted"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>

            {/* Search */}
            <div className="flex items-center gap-2 px-5 py-3 border-b border-line">
              <Search size={14} className="text-ink-subtle" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search shortcuts..."
                className="flex-1 bg-transparent text-sm text-ink placeholder:text-ink-subtle outline-none"
                autoFocus
              />
            </div>

            {/* Shortcut groups */}
            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {(Object.keys(CONTEXT_LABEL) as ShortcutContext[]).map((ctx) => {
                const items = grouped[ctx];
                if (!items || items.length === 0) return null;
                return (
                  <section key={ctx}>
                    <h3 className="text-xs font-semibold text-ink-muted uppercase tracking-wide mb-2">
                      {CONTEXT_LABEL[ctx]}
                    </h3>
                    <div className="space-y-1">
                      {items.map((s) => (
                        <div key={`${ctx}-${s.keys}`} className="flex items-center justify-between py-1 px-2 rounded hover:bg-surface-sunken/50 transition-colors">
                          <span className="text-sm text-ink">{s.label}</span>
                          <div className="flex items-center gap-1">
                            {s.keys.split(' ').map((k, i) => (
                              <Kbd key={i}>{k}</Kbd>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                );
              })}

              {Object.values(grouped).flat().length === 0 && (
                <div className="text-center text-sm text-ink-subtle py-8">
                  No shortcuts match "{search}"
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-5 py-2 border-t border-line bg-surface-sunken/50 flex items-center justify-between text-[10px] text-ink-subtle">
              <span>Auto-generated from shortcut registry · always in sync</span>
              <span><Kbd>esc</Kbd> to close</span>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
